<template>
  <component :is="tourLayout">
    <div v-if="hub?.page" class="w-full">
      <AppBreadcrumb :trail="trail" />

      <header
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 my-4 flex flex-col gap-2"
      >
        <h1 class="text-3xl font-black">{{ heading }}</h1>
        <p class="max-w-4xl text-gray-300">{{ intro }}</p>
        <p v-if="isPair" class="max-w-4xl text-gray-400 text-sm">
          Browse all
          <template v-for="(tag, i) in hub.tags" :key="`all-${tag.slug}`">
            <!-- Explicit spaces: the template compiler drops the whitespace
                 around these links. -->
            <template v-if="i > 0">{{ " or all " }}</template>
            <NuxtLink :to="`/tags/${tag.slug}`" class="underline">
              {{ tag.label }}
            </NuxtLink>
          </template>
          {{ " scenes." }}
        </p>
      </header>

      <SectionContainer id="latest-videos">
        <template #header>
          <h2 class="text-2xl">Latest Videos</h2>
        </template>
        <div class="card-grid">
          <ReleaseCard
            v-for="release in hub.releases.items"
            :key="release.id"
            :release="release"
            :vertical="useVerticalCards"
          />
        </div>
      </SectionContainer>

      <Pagination
        :resource="path.slice(1)"
        :current-page="page"
        :total-pages="totalPages"
      />

      <SectionContainer v-if="pairs.length" id="narrow">
        <template #header>
          <h2 class="text-2xl">Narrow it down</h2>
        </template>
        <ul class="flex flex-wrap gap-2">
          <li v-for="tag in pairs" :key="`pair-${tag.slug}`">
            <NuxtLink
              :to="pairPath(tag.slug)"
              class="block px-2 py-1 border border-gray-700 rounded text-sm hover:border-gray-400"
            >
              {{ hub.page.label }} + {{ tag.label }}
              <span class="text-gray-400">({{ tag.together }})</span>
            </NuxtLink>
          </li>
        </ul>
      </SectionContainer>

      <SectionContainer v-if="hub.related.length" id="related-tags">
        <template #header>
          <h2 class="text-2xl">Related tags</h2>
        </template>
        <ul class="flex flex-wrap gap-2">
          <li v-for="tag in hub.related" :key="`related-${tag.slug}`">
            <NuxtLink
              :to="`/tags/${tag.slug}`"
              class="block px-2 py-1 border border-gray-700 rounded text-sm hover:border-gray-400"
            >
              {{ tag.label }}
              <span class="text-gray-400">({{ tag.sceneCount }})</span>
            </NuxtLink>
          </li>
        </ul>
      </SectionContainer>
    </div>
  </component>
</template>

<script setup>
  // A tag hub page: one tag (/tags/{slug}) or the scenes two tags share
  // (/tags/{a}/{b}). The API answers an alias, a variant spelling or a pair in
  // the wrong order with the canonical path, which becomes a 301 here.
  defineOptions({
    name: "TagHub",
  })

  const props = defineProps({
    slugs: {
      type: Array,
      required: true,
    },
  })

  useRedirectInAgeRestrictedArea()

  const currentSite = inject("currentSite")
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")
  const route = useRoute()
  const { notFoundUrl, useVerticalCards } = useSiteSettings(currentSite.value)
  const tourLayout = useTourLayout(currentSite.value)
  const page = Math.max(1, parseInt(route.query.page) || 1)
  const perPage = 24
  const path = `/tags/${props.slugs.map(encodeURIComponent).join("/")}`
  const isPair = props.slugs.length > 1

  const { data: hub } = await useApi(`/api${path}`, {
    query: { page, age_restricted: inAgeRestrictedLocation },
  })

  const totalPages = computed(
    () => hub.value?.releases?.pagination?.totalPages || 1,
  )

  if (hub.value?.redirect) {
    const target =
      page > 1 ? `${hub.value.redirect}?page=${page}` : hub.value.redirect
    await navigateTo(target, { redirectCode: 301 })
  } else if (!hub.value?.page) {
    await navigateTo(notFoundUrl, { external: true, redirectCode: 302 })
  } else if (page > totalPages.value) {
    await navigateTo(path, { redirectCode: 302 })
  }

  const siteName = computed(() => currentSite.value.name)

  // A pair page: "Anal Blowjob Videos", titled "Anal Blowjob Videos | Passion
  // HD". A tag page: "Anal scenes in 4K on Passion HD", which is its title too.
  const heading = computed(() => {
    if (!hub.value?.page) return ""
    if (isPair) return pairHeading(hub.value.tags)

    const uhd = hub.value.page.mostlyUhd ? " in 4K" : ""
    return `${tagNames(hub.value.tags)} scenes${uhd} on ${siteName.value}`
  })

  const title = computed(() =>
    isPair ? `${heading.value} | ${siteName.value}` : heading.value,
  )

  // A pair page: one generated sentence naming the count, worded per pair.
  // A tag page: the hand-written line, then the count. No newest-scene date:
  // some sites are no longer updated, and the page shouldn't say so.
  const intro = computed(() => {
    if (!hub.value?.page) return ""

    const count = hub.value.page.sceneCount
    if (isPair) {
      return pairIntro({
        tags: hub.value.tags,
        siteName: siteName.value,
        count,
        mostlyUhd: hub.value.page.mostlyUhd,
        copy: siteCopyFor(currentSite.value),
      })
    }

    const facts = `${count.toLocaleString("en-US")} ${count === 1 ? "scene" : "scenes"}.`
    const [tag] = hub.value.tags
    return `${tagIntro({ slug: tag.slug, label: tag.label, siteName: siteName.value })} ${facts}`
  })

  // Only single-tag pages offer pair pages; a pair page's related tags go to
  // their own pages.
  const pairs = computed(() =>
    isPair ? [] : (hub.value?.related ?? []).filter((tag) => tag.pair),
  )

  const pairPath = (other) =>
    `/tags/${[props.slugs[0], other].sort().join("/")}`

  const trail = computed(() => {
    const crumbs = [
      { name: siteName.value, path: "/" },
      { name: "Tags", path: "/tags" },
    ]
    if (isPair) {
      hub.value.tags.forEach((tag) =>
        crumbs.push({ name: tag.label, path: `/tags/${tag.slug}` }),
      )
      crumbs.push({ name: `${tagNames(hub.value.tags)}` })
    } else {
      crumbs.push({ name: hub.value?.tags?.[0]?.label })
    }
    return crumbs
  })

  if (hub.value?.page) {
    useSiteSeo(
      {
        defaultTitle: "{{{ title }}}",
        defaultDescription: "{{{ intro }}}",
        title: title.value,
        intro: intro.value,
      },
      {},
      {
        // Tag copy follows the SEO guide in code, not per-site templates.
        siteTemplates: false,
        canonicalPage: page,
        // A stale page, or a thin pair page (fewer scenes than
        // SiteTags::MIN_PAIR_SCENES), is served but not indexed; links
        // through it still count. The API decides from the scene count, so
        // pages move in and out of the index as scenes are released.
        noindex: hub.value.page.stale || hub.value.page.thin,
      },
    )

    const origin = publicSiteOrigin(useRequestURL().href)
    const schema = computed(() =>
      buildCollectionPageSchema({
        origin,
        url: `${origin}${page > 1 ? `${path}?page=${page}` : path}`,
        name: heading.value,
        description: intro.value,
        trail: trail.value,
        items: (hub.value?.releases?.items ?? []).map((release) => ({
          path: `/video/${release.cachedSlug}`,
          name: release.title,
        })),
        startPosition: (page - 1) * perPage + 1,
      }),
    )

    useHead({
      script: computed(() =>
        schema.value
          ? [
              {
                key: "schema-CollectionPage",
                type: "application/ld+json",
                innerHTML: serializeJsonLd(schema.value),
              },
            ]
          : [],
      ),
    })
  }
</script>
